// ─────────────────────────────────────────────────────────────────────────────
// FrankoPop — real API client.
//
// All business rules (admin-only config, contents lock on release, LTD caps,
// limited-run stock, giftable-only vs shop, staff weekly gift limit, framp
// balances, the full per-FrankoPop ledger, ...) are enforced server-side in
// franklyapi (src/services/frankopopService.js) against real MariaDB tables.
// This module is a thin fetch wrapper - it does not duplicate any of those rules.
//
// Conceptual structure: Set -> Box -> Case -> Frankopop(figure/instance)
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchPublic, fetchWithMessage } from '../services/api';

export const CURRENCY = 'framp cards';
export const FRAMP_UNIT = 'framps';
export const SCRAP_UNIT = 'scraps';
export const STAMP_CARD_VALUE = 10;
export const STAFF_WEEKLY_LIMIT = 5;
export const QUICK_SELL_REFUND = 3;

export const actorFromUser = (userData) => ({
    isAdmin: !!userData?.hasAdminRole,
    isStaff: !!userData?.hasStaffRole,
    name: userData?.habboName || userData?.username || 'You',
});

// ── Small fetch-on-mount hook shared by every FrankoPop page ──────────────────
// Returns { data, loading, error, refetch }. `fetchFn` must be stable (wrap it in
// useCallback at the call site) since it re-runs whenever the identity changes.
export function useFrankopopData(fetchFn, initial = null) {
    const [data, setData] = useState(initial);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    const run = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await fetchFn();
            if (mounted.current) setData(result);
        } catch (err) {
            if (mounted.current) setError(err.message || 'Something went wrong.');
        } finally {
            if (mounted.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchFn]);

    useEffect(() => { run(); }, [run]);

    return { data, loading, error, refetch: run };
}

// ── Reads ───────────────────────────────────────────────────────────────────
export const fetchSets = async () => (await fetchPublic('frankopop/sets')).sets;
export const fetchSet = async (setId) => fetchPublic(`frankopop/sets/${encodeURIComponent(setId)}`);
export const fetchWallet = async () => fetchPublic('frankopop/wallet');
export const fetchMyBoxes = async () => (await fetchPublic('frankopop/my/boxes')).boxes;
export const fetchMyPops = async () => (await fetchPublic('frankopop/my/pops')).pops;
export const fetchMyStats = async () => (await fetchPublic('frankopop/my/stats')).stats;
export const fetchActivity = async (page = 1, pageSize = 20) =>
    fetchPublic(`frankopop/activity?page=${page}&pageSize=${pageSize}`);
export const fetchInstanceHistory = async (instanceId) => fetchPublic(`frankopop/instances/${encodeURIComponent(instanceId)}`);

export const fetchEligibleSets = async () => (await fetchPublic('frankopop/gift/eligible-sets')).sets;
export const fetchRecentGifts = async () => (await fetchPublic('frankopop/gift/recent')).gifts;
export const fetchWeeklyUsage = async (setId) => fetchPublic(`frankopop/gift/usage/${encodeURIComponent(setId)}`);

export const fetchAdminSets = async () => (await fetchPublic('frankopop/admin/sets')).sets;
export const fetchAdminSet = async (setId) => fetchPublic(`frankopop/admin/sets/${encodeURIComponent(setId)}`);
export const fetchRarities = async () => (await fetchPublic('frankopop/admin/rarities')).rarities;
export const fetchSetQuickSellConfig = async (setId) => (await fetchPublic(`frankopop/admin/sets/${encodeURIComponent(setId)}/quick-sell-config`)).rarities;
export const fetchEffectiveOdds = async (setId) => (await fetchPublic(`frankopop/admin/sets/${encodeURIComponent(setId)}/effective-odds`)).rarities;
export const fetchAdminInstanceHistory = async (instanceId) => fetchPublic(`frankopop/admin/instances/${encodeURIComponent(instanceId)}`);
export const fetchAuditFeed = async (limit = 50) => (await fetchPublic(`frankopop/admin/audit?limit=${limit}`)).events;
export const searchLedger = async (query) => (await fetchPublic(`frankopop/admin/ledger/search?q=${encodeURIComponent(query)}`)).results;

// ── Trading ─────────────────────────────────────────────────────────────────
export const PACKAGING_LABELS = { any: 'Either', boxed: 'In packaging', unboxed: 'Out of packaging' };

export const fetchMarketplace = async ({ page = 1, pageSize = 20, setId = null } = {}) =>
    fetchPublic(`frankopop/trades/listings?page=${page}&pageSize=${pageSize}${setId ? `&setId=${encodeURIComponent(setId)}` : ''}`);
export const fetchListing = async (listingId) =>
    (await fetchPublic(`frankopop/trades/listings/${encodeURIComponent(listingId)}`)).listing;
export const fetchMyTrades = async () => fetchPublic('frankopop/trades/mine');

export const createListing = ({ instanceIds, wants, note }) =>
    fetchWithMessage('frankopop/trades/listings', { method: 'POST', body: JSON.stringify({ instanceIds, wants, note }) });
export const cancelListing = (listingId) =>
    fetchWithMessage(`frankopop/trades/listings/${encodeURIComponent(listingId)}/cancel`, { method: 'POST' });
export const acceptListing = (listingId, instanceIds) =>
    fetchWithMessage(`frankopop/trades/listings/${encodeURIComponent(listingId)}/accept`, { method: 'POST', body: JSON.stringify({ instanceIds }) });
export const makeOffer = (listingId, { instanceIds, note }) =>
    fetchWithMessage(`frankopop/trades/listings/${encodeURIComponent(listingId)}/offers`, { method: 'POST', body: JSON.stringify({ instanceIds, note }) });
export const acceptOffer = (offerId) =>
    fetchWithMessage(`frankopop/trades/offers/${encodeURIComponent(offerId)}/accept`, { method: 'POST' });
export const declineOffer = (offerId) =>
    fetchWithMessage(`frankopop/trades/offers/${encodeURIComponent(offerId)}/decline`, { method: 'POST' });
export const withdrawOffer = (offerId) =>
    fetchWithMessage(`frankopop/trades/offers/${encodeURIComponent(offerId)}/withdraw`, { method: 'POST' });

// ── Player mutations ───────────────────────────────────────────────────────
export const purchaseBox = (setId) =>
    fetchWithMessage('frankopop/purchase', { method: 'POST', body: JSON.stringify({ setId }) });

export const openBox = (boxId) =>
    fetchWithMessage('frankopop/open', { method: 'POST', body: JSON.stringify({ boxId }) });

export const unpackagePop = (instanceId) =>
    fetchWithMessage('frankopop/unpackage', { method: 'POST', body: JSON.stringify({ instanceId }) });

export const quickSellPop = (instanceId) =>
    fetchWithMessage('frankopop/quick-sell', { method: 'POST', body: JSON.stringify({ instanceId }) });

export const quickSellManyPops = (instanceIds) =>
    fetchWithMessage('frankopop/quick-sell-many', { method: 'POST', body: JSON.stringify({ instanceIds }) });

// ── Staff / admin: gifting ─────────────────────────────────────────────────
export const giftBox = ({ setId, recipientHabboName, forcedFigureId }) =>
    fetchWithMessage('frankopop/gift', {
        method: 'POST',
        body: JSON.stringify({ setId, recipientHabboName, forcedFigureId }),
    });

// ── Admin: sets ────────────────────────────────────────────────────────────
export const adminCreateSet = (payload) =>
    fetchWithMessage('frankopop/admin/sets', { method: 'POST', body: JSON.stringify(payload) });

export const adminUpdateSet = (setId, payload) =>
    fetchWithMessage(`frankopop/admin/sets/${encodeURIComponent(setId)}`, { method: 'PUT', body: JSON.stringify(payload) });

export const adminDeleteSet = (setId) =>
    fetchWithMessage(`frankopop/admin/sets/${encodeURIComponent(setId)}`, { method: 'DELETE' });

// ── Admin: figures (set contents) ──────────────────────────────────────────
export const adminUpsertFigure = (setId, input) => {
    const path = input.id
        ? `frankopop/admin/sets/${encodeURIComponent(setId)}/figures/${encodeURIComponent(input.id)}`
        : `frankopop/admin/sets/${encodeURIComponent(setId)}/figures`;
    return fetchWithMessage(path, { method: input.id ? 'PUT' : 'POST', body: JSON.stringify(input) });
};

export const adminDeleteFigure = (setId, figureId) =>
    fetchWithMessage(`frankopop/admin/sets/${encodeURIComponent(setId)}/figures/${encodeURIComponent(figureId)}`, { method: 'DELETE' });

// ── Admin: per-set quick-sell values (chance is global - see Admin: rarities) ──
export const adminSetQuickSellValues = (setId, rarities) =>
    fetchWithMessage(`frankopop/admin/sets/${encodeURIComponent(setId)}/quick-sell-config`, {
        method: 'PUT',
        body: JSON.stringify({ rarities }),
    });

// ── Admin: rarities (global chance and scrap value live here, not per set) ─
export const adminCreateRarity = (name, chancePercent, scrapValue) =>
    fetchWithMessage('frankopop/admin/rarities', { method: 'POST', body: JSON.stringify({ name, chancePercent, scrapValue }) });

export const adminUpdateRarity = (rarityId, { name, chancePercent, scrapValue }) =>
    fetchWithMessage(`frankopop/admin/rarities/${encodeURIComponent(rarityId)}`, { method: 'PUT', body: JSON.stringify({ name, chancePercent, scrapValue }) });

export const adminSetBaseRarity = (rarityId) =>
    fetchWithMessage(`frankopop/admin/rarities/${encodeURIComponent(rarityId)}/set-base`, { method: 'POST' });

export const adminDeleteRarity = (rarityId) =>
    fetchWithMessage(`frankopop/admin/rarities/${encodeURIComponent(rarityId)}`, { method: 'DELETE' });

// ── Display metadata ────────────────────────────────────────────────────────
export const STATUS_META = {
    'draft': { label: 'Draft', colour: '#6b7280' },
    'scheduled': { label: 'Scheduled', colour: '#2563eb' },
    'on-sale': { label: 'On sale', colour: '#16a34a' },
    'ended': { label: 'Ended', colour: '#6b7280' },
    'sold-out': { label: 'Sold out', colour: '#b91c1c' },
    'gift-only': { label: 'Gift only', colour: '#c47f17' },
};

// ── Formatting helpers ──────────────────────────────────────────────────────
export const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d)) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d)) return '-';
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const relativeTime = (value) => {
    const d = new Date(value);
    if (isNaN(d)) return '';
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
};

// epoch ms -> value for <input type="datetime-local">
export const toLocalInput = (ms) => {
    if (ms == null) return '';
    const d = new Date(ms);
    if (isNaN(d)) return '';
    const off = d.getTimezoneOffset() * 60000;
    return new Date(ms - off).toISOString().slice(0, 16);
};
